import React from 'react';
import { useForm } from 'react-hook-form';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { Button } from 'primereact/button';

import FormInputText from '../../components/form/FormInputText';
import { IntegrationSettings } from '../../models/integrationSettings';
import { IntegrationStatus, IntegrationVendor } from '../../models/enums/integrationEnums';
import { parseIntegrationData } from '../../utils/misc-utils';

type ErnstFeeFormValues = {
  username: string;
  password: string;
};

interface EditErnstFeeIntegrationSettingsProps {
  onCancel: () => void;
  onSave?: (settings: IntegrationSettings) => void;
  integrationSettings?: IntegrationSettings;
  environment: string;
}

export const EditErnstFeeIntegrationSettings = ({
  onCancel,
  onSave,
  integrationSettings,
  environment,
}: EditErnstFeeIntegrationSettingsProps): JSX.Element => {
  const isEditMode = !!integrationSettings;

  let username = '';
  let password = '';
  if (isEditMode && integrationSettings?.integrationData) {
    const data = parseIntegrationData(integrationSettings.integrationData);
    username = data.username || '';
    password = data.password || '';
  }

  const validationSchema = yup.object().shape({
    username: yup.string().required('Username is required'),
    password: yup.string().required('Password is required'),
  });

  const { control, handleSubmit, formState } = useForm<ErnstFeeFormValues>({
    defaultValues: {
      username: username,
      password: password,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
  });

  const { errors } = formState;

  const onSubmit = (data: ErnstFeeFormValues) => {
    console.log('Form validated successfully:', data);

    if (onSave) {
      let settings: IntegrationSettings;

      if (isEditMode && integrationSettings) {
        settings = {
          ...integrationSettings,
          environment: environment,
          integrationData: JSON.stringify({
            username: data.username,
            password: data.password,
          }),
        };
      } else {
        settings = {
          name: 'Ernst Fee Integration',
          vendor: IntegrationVendor.Ernst,
          status: IntegrationStatus.Active,
          environment: environment,
          integrationData: JSON.stringify({
            username: data.username,
            password: data.password,
          }),
        };
      }

      onSave(settings);
    }
  };

  return (
    <div className="surface-section p-4 border-1 surface-border border-round mb-3">
      <h3 className="mt-0 mb-3">{isEditMode ? 'Edit Ernst Fee Settings' : 'Ernst Fee Settings'}</h3>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="pb-3 p-fluid p-input-filled">
        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="username"
            label="Username*"
            placeholder="Enter your Ernst Fee Username"
          />
        </div>

        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="password"
            label="Password*"
            placeholder="Enter your Ernst Fee Password"
          />
        </div>

        <div className="flex justify-content-end gap-2 pt-4">
          <Button
            type="button"
            label="Cancel"
            className="p-button-outlined"
            style={{ width: 'auto', minWidth: 'auto' }}
            onClick={onCancel}
          />
          <Button
            type="submit"
            label={isEditMode ? 'Update' : 'Save'}
            className="p-button"
            style={{ width: 'auto', minWidth: 'auto' }}
          />
        </div>
      </form>
    </div>
  );
};
